import { Injectable } from '@angular/core';
import { IHealthStatus } from '@app/app.interface';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';
import { MaintenanceService } from './maintenance.service';

interface IMaintenanceState {
  error: string | null;
  health: IHealthStatus | null;
  loading: boolean;
}

const initialState: IMaintenanceState = {
  error: null,
  health: null,
  loading: false,
};

@Injectable()
export class MaintenanceStore extends ComponentStore<IMaintenanceState> {
  public readonly health$ = this.selectSignal((state: IMaintenanceState) => state.health);
  public readonly loading$ = this.selectSignal((state: IMaintenanceState) => state.loading);
  public readonly error$ = this.selectSignal((state: IMaintenanceState) => state.error);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  constructor(private readonly maintenanceService: MaintenanceService) {
    super(initialState);
  }

  public readonly fetchHealth = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap(() =>
        this.maintenanceService.getHealthStatus().pipe(
          tap((health: IHealthStatus) => {
            this.patchState({ health, loading: false });
          }),
          this.handleError('Failed to load health status'),
        ),
      ),
    );
  });
}
